import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Button from "./Button"
import * as XLSX from 'xlsx'
import Papa from 'papaparse'
import { isURL } from "../utlis/utlis";
import styles from './ProductListUpload.module.css';
import { toast } from 'react-toastify'
import { useWholesaleFileUploadMutation } from '../services/wholesale'
import Flag from 'react-flagkit'


const MAPPING_PROPS = [
  {
    label: 'Product-ID Reihe',
    key: 'match_row'
  },

  {
    label: 'Kostenreihe*',
    key: 'price_row'
  },
  {
    label: 'Währung*',
    key: 'currency',
    options: [
      { value: 'euro', label: 'EUR' },
      { value: 'usd', label: 'USD' },
      { value: 'gbp', label: 'GBP' },
    ]
  },
  {
    label: 'Produkt-Matching nach',
    key: 'match_type',
    options: [
      { value: 'ean', label: 'EAN' },
      { value: 'gtin', label: 'GTIN' },
      { value: 'upc', label: 'UPC' },
      { value: 'asin', label: 'ASIN' },
      { value: 'isbn', label: 'ISBN' },
    ]
  },
  {
    label: 'Titel (optional)',
    key: 'title_row',
    allowEmpty: true,
  },
  {
    label: 'Extra-Spalte (optional)',
    key: 'extra_row',
    allowEmpty: true,
  }
]

const MARKETPLACE_OPTIONS = [
  { value: 'de', label: 'DE', flag: 'DE' },
  { value: 'it', label: 'IT', flag: 'IT' },
  { value: 'es', label: 'ES', flag: 'ES' },
  { value: 'fr', label: 'FR', flag: 'FR' },
]
const DEFAULT_MARKETPLACE = MARKETPLACE_OPTIONS[0]?.value || 'de'
const MULTI_REGION_MEMBERSHIPS = new Set(['EDGE PRO+', 'EDGE PRO', 'SCAN PRO+'])
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024
const MAX_ROW_COUNT = 100000
const FILE_SIZE_LIMIT_MESSAGE = 'Die Datei darf maximal 20MB groß sein. Bitte reduziere deine Datei.'
const ROW_LIMIT_MESSAGE = 'Die Liste darf maximal 100.000 Zeilen enthalten. Bitte reduziere deine Datei.'

let unquote = (value) => value.replace(/^"(.+(?="$))"$/, '$1')

let isExcelFile = (name = '') => {
  let normalized = String(name).toLowerCase()
  return normalized.endsWith('.xlsx') || normalized.endsWith('.xls') || normalized.endsWith('.xlsm')
}

let isFileTooLarge = (file) => file?.size > MAX_FILE_SIZE_BYTES

let sanitizeHeaderValue = (value, index) => {
  if (value === undefined || value === null) {
    return `Spalte ${index + 1}`
  }
  let text = String(value).trim()
  return text || `Spalte ${index + 1}`
}

let buildHeaders = (row = []) => {
  let seen = new Set()
  return row.map((cell, index) => {
    let base = sanitizeHeaderValue(cell, index)
    let header = base
    let counter = 1
    while (seen.has(header)) {
      header = `${base}_${counter}`
      counter += 1
    }
    seen.add(header)
    return header
  })
}

let buildPreviewFromRows = (rows, headerRowIndex, previewCount = 4) => {
  if (!Array.isArray(rows) || !rows.length || headerRowIndex == null) {
    return { preview: [], headers: [] }
  }

  let headers = buildHeaders(rows[headerRowIndex] || [])
  let preview = []

  for (
    let rowIndex = headerRowIndex + 1;
    rowIndex < Math.min(rows.length, headerRowIndex + 1 + previewCount);
    rowIndex++
  ) {
    let sourceRow = rows[rowIndex] || []
    let item = {}
    headers.forEach((header, colIndex) => {
      item[header] = sourceRow[colIndex] ?? ''
    })
    preview.push(item)
  }

  return { preview, headers }
}

let detectHeaderRowFromRows = (rows) => {
  if (!Array.isArray(rows) || !rows.length) {
    return 0
  }

  let fallbackIndex = null
  let headerRowIndex = 0
  let maxScore = -1

  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 25); rowIndex++) {
    let row = rows[rowIndex] || []
    let normalized = row.map((cell) => {
      if (cell === undefined || cell === null) {
        return ''
      }
      return String(cell).trim()
    })
    let nonEmpty = normalized.filter((value) => value !== '')
    if (nonEmpty.length && fallbackIndex === null) {
      fallbackIndex = rowIndex
    }
    let stringCount = normalized.filter((value) => value && isNaN(Number(value))).length
    let score = nonEmpty.length * 2 + stringCount

    if (nonEmpty.length >= 2 && score > maxScore) {
      maxScore = score
      headerRowIndex = rowIndex
    }
  }

  if (maxScore === -1) {
    return fallbackIndex ?? 0
  }

  return headerRowIndex
}

// Flacht verschachtelte Arrays ab, damit alle Zeilen immer als flache Arrays behandelt werden
let flattenRow = (row) => {
  if (!Array.isArray(row)) {
    return Array.isArray(Object.values(row)) ? Object.values(row) : [row]
  }
  
  let flattened = []
  for (let item of row) {
    if (Array.isArray(item)) {
      // Wenn das Element selbst ein Array ist, flache es rekursiv ab
      flattened.push(...flattenRow(item))
    } else {
      flattened.push(item)
    }
  }
  return flattened
}

let summarizeRow = (row, maxParts = 3) => {
  if (!Array.isArray(row)) {
    return ''
  }

  let parts = row
    .map((cell) => {
      if (cell === undefined || cell === null) {
        return ''
      }
      return String(cell).trim()
    })
    .filter((value) => value !== '')
    .slice(0, maxParts)

  return parts.join(' | ')
}

let ProductListUpload = ({ onClose }) => {
  let [isDragging, setIsDragging] = useState(false)
  let [selectedFile, setSelectedFile] = useState(null)
  let [parsedData, setParsedData] = useState(null)
  let [isParsing, setIsParsing] = useState(false)
  let [error, setError] = useState(null)

  let [rowIndex, setRowIndex] = useState(null)
  let [autoDetectedRowIndex, setAutoDetectedRowIndex] = useState(null)
  let [rawRows, setRawRows] = useState([])
  let [tableHeaders, setTableHeaders] = useState([])
  let { mutateAsync: uploadWholesaleFile, isPending: isUploading } = useWholesaleFileUploadMutation()
  let [usePremiumData, setUsePremiumData] = useState(false)
  let [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  let [customName, setCustomName] = useState('')
  let [isEditingName, setIsEditingName] = useState(false)

  let [mapping, setMapping] = useState({
    marketplace: 'de',
    currency: 'euro',
    match_type: 'ean',
    extra_row: '',
    title_row: '',
  })
  let [selectedMarketplaces, setSelectedMarketplaces] = useState([DEFAULT_MARKETPLACE])
  const membership = useSelector((state) => {
    const homeMembership = state.auth.homeData?.membership
    if (homeMembership) {
      return homeMembership
    }
    return state.auth.authData?.membership
  })
  const normalizedMembership = typeof membership === 'string'
    ? membership.trim().toUpperCase()
    : ''
  const canUseMultiRegion = MULTI_REGION_MEMBERSHIPS.has(normalizedMembership)

  let parsedDataColumns = useMemo(() => tableHeaders, [tableHeaders])

  useEffect(() => {
    let primaryMarketplace = selectedMarketplaces[0] ?? 'de'
    setMapping(prev => {
      if (prev.marketplace === primaryMarketplace) {
        return prev
      }
      return { ...prev, marketplace: primaryMarketplace }
    })
  }, [selectedMarketplaces])

  useEffect(() => {
    if (parsedDataColumns?.length) {
      let normalizedHeaders = parsedDataColumns.map((col) => String(col ?? '').toLowerCase())
      let matchKeywords = ['ean', 'gtin', 'upc']
      let matchRowIndex = normalizedHeaders.findIndex(col => matchKeywords.some(keyword => col.includes(keyword)))
      if (matchRowIndex === -1) {
        matchRowIndex = 0
      }

      let priceRowIndex = normalizedHeaders.findIndex(col => col.includes('price'))
      if (priceRowIndex === -1) {
        priceRowIndex = parsedDataColumns.length > 1 ? 1 : 0
      }

      setMapping(prev => ({
        ...prev,
        match_row: matchRowIndex,
        price_row: priceRowIndex,
      }))
    }
  }, [parsedDataColumns])

  useEffect(() => {
    setMapping(prev => {
      let next = { ...prev }
      let changed = false
      if (prev.extra_row !== '' && (parsedDataColumns.length === 0 || prev.extra_row >= parsedDataColumns.length)) {
        next.extra_row = ''
        changed = true
      }
      if (prev.title_row !== '' && (parsedDataColumns.length === 0 || prev.title_row >= parsedDataColumns.length)) {
        next.title_row = ''
        changed = true
      }
      return changed ? next : prev
    })
  }, [parsedDataColumns])

  let updatePreviewForHeaderRow = useCallback((rowsSource, headerRow) => {
    let { preview, headers } = buildPreviewFromRows(rowsSource, headerRow)
    setRowIndex(headerRow)
    setTableHeaders(headers)
    setParsedData(preview)
  }, [])

  let parseFile = useCallback(async (file) => {
    setIsParsing(true)
    setError(null)
    setParsedData(null)
    setTableHeaders([])
    setRawRows([])
    setRowIndex(null)
    setAutoDetectedRowIndex(null)
    try {
      if (file.type === 'text/csv') {
        Papa.parse(file, {
          escapeChar: '',
          quoteChar: '',
          transform: unquote,
          header: false,
          skipEmptyLines: false,
          complete: (results) => {
            setIsParsing(false)
            // Flache alle Zeilen ab, damit verschachtelte Listen als ein Ganzes behandelt werden
            let rows = results.data.map((row) => {
              let processedRow = Array.isArray(row) ? row : Object.values(row)
              return flattenRow(processedRow)
            })

            if (!rows.length) {
              setError('Die Datei enthält keine Daten')
              return
            }
            if (rows.length > MAX_ROW_COUNT) {
              setError(ROW_LIMIT_MESSAGE)
              toast.info(ROW_LIMIT_MESSAGE)
              setParsedData(null)
              setTableHeaders([])
              setRawRows([])
              return
            }

            setRawRows(rows)
            let detectedHeader = detectHeaderRowFromRows(rows)
            setAutoDetectedRowIndex(detectedHeader)
            updatePreviewForHeaderRow(rows, detectedHeader)
          },
          error: (error) => {
            setError('Error parsing CSV file: ' + error.message)
            setIsParsing(false)
          }
        })
      } else if (isExcelFile(file.name)) {
        let reader = new FileReader()
        reader.onload = (e) => {
          try {
            let data = new Uint8Array(e.target.result)
            let workbook = XLSX.read(data, { type: 'array' })
            let firstSheet = workbook.Sheets[workbook.SheetNames[0]]
            // Verwende sheet_to_json, aber behalte die ursprüngliche Spaltenstruktur bei
            // indem wir die Range des Sheets verwenden, um alle Zellen zu erfassen
            let range = XLSX.utils.decode_range(firstSheet['!ref'] || 'A1')
            let maxCol = range.e.c // Maximale Spalte
            let maxRow = range.e.r // Maximale Zeile
            
            // Erstelle ein Array mit der korrekten Spaltenstruktur
            let sheetRows = []
            for (let R = 0; R <= maxRow; R++) {
              let row = []
              for (let C = 0; C <= maxCol; C++) {
                let cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
                let cell = firstSheet[cellAddress]
                row.push(cell ? (cell.v !== undefined ? cell.v : '') : '')
              }
              sheetRows.push(row)
            }

            if (!sheetRows.length) {
              setError('Die Excel-Datei enthält keine Daten')
            } else {
              if (sheetRows.length > MAX_ROW_COUNT) {
                setError(ROW_LIMIT_MESSAGE)
                toast.info(ROW_LIMIT_MESSAGE)
                setParsedData(null)
                setTableHeaders([])
                setRawRows([])
                return
              }
              // Flache alle Zeilen ab, damit verschachtelte Listen als ein Ganzes behandelt werden
              let flattenedRows = sheetRows.map((row) => flattenRow(row))
              setRawRows(flattenedRows)
              let detectedHeader = detectHeaderRowFromRows(flattenedRows)
              setAutoDetectedRowIndex(detectedHeader)
              updatePreviewForHeaderRow(flattenedRows, detectedHeader)
            }
          } catch (error) {
            setError('Error parsing Excel file: ' + error.message)
          }
          setIsParsing(false)
        }
        reader.onerror = () => {
          setError('Error reading file')
          setIsParsing(false)
        }
        reader.readAsArrayBuffer(file)
      }
    } catch (error) {
      setError('Error processing file: ' + error.message)
      setIsParsing(false)
    }
  }, [updatePreviewForHeaderRow])

  let handleHeaderRowChange = useCallback((newIndex) => {
    if (!rawRows?.length) {
      return
    }

    if (Number.isNaN(newIndex)) {
      return
    }

    let boundedIndex = Math.max(0, Math.min(newIndex, rawRows.length - 1))
    setError(null)
    updatePreviewForHeaderRow(rawRows, boundedIndex)
  }, [rawRows, updatePreviewForHeaderRow])

  let headerRowOptions = useMemo(() => {
    if (!rawRows?.length) {
      return []
    }

    let maxOptions = Math.min(rawRows.length, 25)

    let options = Array.from({ length: maxOptions }, (_, index) => {
      let summary = summarizeRow(rawRows[index])
      let label = `Zeile ${index + 1}`
      if (summary) {
        label += `: ${summary}`
      }
      if (!summary) {
        label += ' (leer)'
      }
      if (index === autoDetectedRowIndex) {
        label += ' (auto)'
      }
      return {
        value: index,
        label
      }
    })

    let ensureOptionExists = (targetIndex, postfix = '') => {
      if (
        targetIndex == null ||
        targetIndex < 0 ||
        targetIndex >= rawRows.length ||
        options.some((option) => option.value === targetIndex)
      ) {
        return
      }

      let summary = summarizeRow(rawRows[targetIndex])
      let label = `Zeile ${targetIndex + 1}`
      if (summary) {
        label += `: ${summary}`
      } else {
        label += ' (leer)'
      }
      if (postfix) {
        label += ` ${postfix}`
      }

      options.unshift({ value: targetIndex, label })
    }

    ensureOptionExists(autoDetectedRowIndex, '(auto)')
    ensureOptionExists(rowIndex)

    return options
  }, [rawRows, autoDetectedRowIndex, rowIndex])

  let handleHeaderRowSelectChange = useCallback((event) => {
    let value = Number(event.target.value)
    if (Number.isNaN(value)) {
      return
    }
    handleHeaderRowChange(value)
  }, [handleHeaderRowChange])

  let handleDragOver = useCallback((e) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  let handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  let handleDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragging(false)

    let files = e.dataTransfer.files
    if (files.length > 0) {
      let file = files[0]
      if (isFileTooLarge(file)) {
        setError(FILE_SIZE_LIMIT_MESSAGE)
        toast.info(FILE_SIZE_LIMIT_MESSAGE)
        setSelectedFile(null)
        return
      }
      if (file.type === 'text/csv' || isExcelFile(file.name)) {
        setSelectedFile(file)
        parseFile(file)
      } else {
        setError('Please upload only CSV or Excel files')
      }
    }
  }, [parseFile])

  useEffect(() => {
    if (canUseMultiRegion) {
      return
    }
    setSelectedMarketplaces((prev = []) => {
      if (!Array.isArray(prev) || !prev.length) {
        return [DEFAULT_MARKETPLACE]
      }
      if (prev.length === 1) {
        return prev
      }
      return [prev[0] ?? DEFAULT_MARKETPLACE]
    })
  }, [canUseMultiRegion])

  let toggleMarketplace = useCallback((marketplace) => {
    setSelectedMarketplaces((prev = []) => {
      if (!canUseMultiRegion) {
        if (prev[0] === marketplace) {
          return prev
        }
        return [marketplace]
      }

      if (prev.includes(marketplace)) {
        if (prev.length === 1) {
          return prev
        }
        return prev.filter((item) => item !== marketplace)
      }
      return [...prev, marketplace]
    })
  }, [canUseMultiRegion])

  let handleFileSelect = (e) => {
    let file = e.target.files[0]
    if (file) {
      if (isFileTooLarge(file)) {
        setError(FILE_SIZE_LIMIT_MESSAGE)
        toast.info(FILE_SIZE_LIMIT_MESSAGE)
        setSelectedFile(null)
        return
      }
      if (file.type === 'text/csv' || isExcelFile(file.name)) {
        setSelectedFile(file)
        parseFile(file)
      } else {
        setError('Please upload only CSV or Excel files')
      }
    }
  }

  let handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setError('Bitte zuerst eine Datei auswählen');
      return;
    }

    if (rowIndex === null) {
      setError('Bitte eine Header-Zeile auswählen');
      return;
    }

    if (!selectedMarketplaces.length) {
      setError('Bitte mindestens einen Ziel-Marktplatz auswählen');
      return;
    }

    setError(null);
    const marketplacePayload = MARKETPLACE_OPTIONS.reduce((acc, option) => {
      acc[option.value] = selectedMarketplaces.includes(option.value) ? 'true' : 'false'
      return acc
    }, {})

    const trimmedCustomName = customName.trim()
    const res = await uploadWholesaleFile({
      file: selectedFile,
      data: {
        ...mapping,
        rowIndex,
        premium_data: usePremiumData ? 'true' : 'false',
        ...marketplacePayload,
        ...(trimmedCustomName ? { custom_name: trimmedCustomName } : {}),
      }
    });
    if (res?.success) {
      toast.success('Datei erfolgreich hochgeladen');
      onClose?.();
    } else {
      toast.error('Fehler beim Hochladen');
    }
  }, [selectedFile, mapping, rowIndex, onClose, selectedMarketplaces, uploadWholesaleFile, usePremiumData, customName]);

  return (
    <div className="flex flex-col gap-4 items-start bg-background p-8 rounded-lg w-[360px] sm:w-[600px] md:w-[700px] lg:w-[700px] xl:w-[1000px] relative overflow-auto">
      <div className="bg-background flex flex-col justify-between items-start">
        <p className="title text-xl font-semibold bg-gradient-to-r from-primary to-main text-transparent bg-clip-text">
          Liste
          <span className="text-textTitle ml-1">hochladen</span>
        </p>
        <p className="text-sm subtitle2">Lade eine CSV oder Excel Liste hoch</p>
      </div>

      {parsedData ?
        <div className='w-full flex flex-col gap-4'>
          <div className='space-y-1'>
            {isEditingName ? (
              <input
                type='text'
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setIsEditingName(false)
                    e.currentTarget.blur()
                  }
                }}
                placeholder={selectedFile?.name || 'Listenname'}
                maxLength={255}
                autoFocus
                className='text-lg font-semibold p-1 -ml-1 border-b-2 border-primary bg-transparent text-textTitle dark:text-white outline-none w-full'
              />
            ) : (
              <button
                type='button'
                onClick={() => {
                  if (!customName.trim() && selectedFile?.name) {
                    setCustomName(selectedFile.name)
                  }
                  setIsEditingName(true)
                }}
                className='group flex items-center gap-2 text-lg font-semibold text-textTitle dark:text-white hover:text-primary dark:hover:text-primary transition-colors'
                title='Klicken zum Bearbeiten'
              >
                <span className='border-b border-dashed border-transparent group-hover:border-primary'>
                  {customName.trim() || selectedFile?.name || 'Listenname'}
                </span>
                <i className='bx bx-edit-alt text-base opacity-40 group-hover:opacity-100' />
              </button>
            )}
            <p className='text-[11px] text-gray-500'>Klick zum Umbenennen</p>
          </div>

          <div className='space-y-2'>
            <div className='text-xs text-gray-500'>Header-Zeile auswählen</div>
            <select
              className='p-2 border rounded-md w-full dark:bg-neutral-900 dark:text-white dark:border-neutral-700'
              value={rowIndex ?? ''}
              onChange={handleHeaderRowSelectChange}
              disabled={!headerRowOptions.length}
            >
              <option value='' disabled>Bitte auswählen</option>
              {headerRowOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <p className='text-[11px] text-gray-500'>Wähle die Zeile mit den Spaltennamen.</p>
            {autoDetectedRowIndex !== null && (
              <p className='text-[11px] text-gray-500'>Automatisch erkannt: Zeile {autoDetectedRowIndex + 1}</p>
            )}
          </div>

          {tableHeaders.length ? (
            <>
              <div className="w-full overflow-auto">
                <table className={`text-xs w-full ${styles.table}`}>
                  <thead>
                    <tr>
                      {tableHeaders.map((header) => (
                        <th
                          key={header}
                          className="bg-gray-100 dark:bg-neutral-900"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.length ? parsedData.map((row, index) => (
                      <tr key={index}>
                        {tableHeaders.map((header, colIndex) => {
                          let value = row?.[header]
                          let key = `${index}-${colIndex}-${header}`
                          return (
                            <td key={key} className='truncate ellipsis'>
                              {value ? (
                                isURL(value) ? (
                                  <a href={value} target="_blank" rel="noopener noreferrer" title={value} className='flex items-center gap-1'>
                                    <img src='/static/icons/export.svg' alt='link' className="w-4 h-4 invert" />
                                    <span className="text-xs max-w-[100px] truncate ellipsis">{value}</span>
                                  </a>
                                ) : (
                                  value
                                )
                              ) : ''}
                            </td>
                          )
                        })}
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={tableHeaders.length || 1} className='text-center py-4 text-xs text-gray-500'>Keine Vorschau verfügbar</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {parsedData.length === 0 && (
                <p className='text-[11px] text-amber-600 dark:text-amber-400'>
                  Achtung: Stelle sicher, dass deine Liste Spalten enthält und du beim Hochladen alle Spalten korrekt zugeordnet hast.
                </p>
              )}
            </>
          ) : (
            <p className='text-sm text-gray-500'>Keine Spalten erkannt. Bitte eine andere Header-Zeile wählen.</p>
          )}

          <div className='w-full'>
            <div className='text-xs text-gray-500 mb-1'>
              Ziel-Marktplätze*
            </div>
            <div className='flex flex-wrap gap-2'>
              {MARKETPLACE_OPTIONS.map((option) => {
                let isActive = selectedMarketplaces.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type='button'
                    onClick={() => toggleMarketplace(option.value)}
                    className={`flex-1 min-w-[110px] flex items-center justify-center gap-2 px-3 py-2 rounded-md border transition-colors ${
                      isActive
                        ? 'bg-gradient-to-br from-primary to-main text-white dark:text-black border-transparent'
                        : 'bg-background text-gray-700 border-neutral-300 dark:border-neutral-700 dark:text-white'
                    }`}
                  >
                    <Flag country={option.flag} size={16} aria-label={option.label} />
                    <span className='text-sm'>{option.label}</span>
                  </button>
                )
              })}
            </div>
            {/* {!canUseMultiRegion && (
              <p className='mt-2 text-[11px] text-gray-500'>
                Dein aktuelles Paket erlaubt nur einen Markt pro Upload.{' '}
                <button
                  type='button'
                  className='text-primary underline decoration-dotted underline-offset-2'
                  onClick={() => window.open('/settings', '_blank')}
                >
                  Upgrade ansehen
                </button>
              </p>
            )} */}
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible'>
            {MAPPING_PROPS.map((prop) => (
              <div key={prop.key}>
                <div className='text-xs text-gray-500 mb-1'>
                  {prop.label}
                </div>
                <select
                  className='p-2 border rounded-md w-full dark:bg-neutral-900 dark:text-white dark:border-neutral-700'
                  value={mapping[prop.key] ?? ''}
                  onChange={(e) => setMapping(prev => ({
                    ...prev,
                    [prop.key]: prop.options
                      ? e.target.value
                      : e.target.value === '' ? '' : Number(e.target.value)
                  }))}
                  disabled={!prop.options && !parsedDataColumns.length}
                >
                  {prop.allowEmpty && (
                    <option value=''>Keine</option>
                  )}
                  {prop.options ?
                    prop.options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    )) :
                    parsedDataColumns.map((column, columnIndex) => (
                      <option key={`${column}-${columnIndex}`} value={columnIndex}>{column}</option>
                    ))
                  }
                </select>
              </div>
            ))}
          </div>

          {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          <div className='w-full flex justify-end'>
            <button
              type='button'
              className='text-[11px] text-gray-400 hover:text-gray-600 dark:text-gray-500 underline decoration-dotted'
              onClick={() => setShowAdvancedOptions(prev => !prev)}
            >
              Weitere Optionen
            </button>
          </div>
          {showAdvancedOptions && (
            <div className='w-full flex flex-col gap-3'>
              <label htmlFor='premium-data' className='w-full flex items-center gap-2 cursor-pointer text-xs text-gray-500 dark:text-gray-400'>
                <input
                  id='premium-data'
                  type='checkbox'
                  className='accent-primary h-3 w-3'
                  checked={usePremiumData}
                  onChange={(e) => setUsePremiumData(e.target.checked)}
                />
                <div className='flex-1 leading-tight'>
                  <span className='font-medium text-textTitle dark:text-white text-sm'>Premium-Daten</span>
                  <span className='ml-2'>Nur wählen, wenn zwingend nötig.</span>
                </div>
              </label>
            </div>
          )}
        </div> :
        <div
          className={`w-full flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 transition-colors duration-200 ${isDragging ? 'border-primary bg-primary/5' : 'border-neutral-300 dark:border-neutral-700'
            }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".csv,.xlsx,.xls,.xlsm"
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            <div className="mb-4">
              <img src="/static/icons/upload.svg" alt="upload" />
            </div>
            {selectedFile ? (
              <div className="text-center">
                <p className="text-sm font-medium text-primary">{selectedFile.name}</p>
                <p className="text-xs text-gray-500 mt-1">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                {isParsing && <p className="text-sm text-gray-500 mt-2">Processing file...</p>}
                {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
                {parsedData && !isParsing && !error && (
                  <p className="text-sm text-green-500 mt-2">
                    Successfully parsed {parsedData.length} rows
                  </p>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm subtitle2">Drag and drop a file</p>
                <p className="text-sm subtitle2">Click to select document</p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-2 text-center">Maximale Dateigröße: 20MB · Max. 100.000 Zeilen pro Liste</p>
          </label>
        </div>
      }

      <div className='mt-6 flex gap-2 self-end'>
        <Button variant='info' onClick={onClose}>Abbrechen</Button>
        <Button variant='primary' onClick={handleUpload} disabled={isUploading || !selectedFile}>Hochladen</Button>
      </div>
    </div>
  );
};

export default ProductListUpload;
